import PublicGoogleSheetsParser from 'public-google-sheets-parser'
import type { CatalogItem } from '@/types/catalog'

/* Keys som representerar prisfält och kan vara feldetekterade datum */
const PRICE_FIELDS = [
  'WSCAD', 'MSRPCAD',
  'WSUSD', 'MSRPUSD',
  'WSEUR', 'MSRPEUR', 'DISTEUR',
  'WSGBP', 'MSRPGBP',
  'WSCHF', 'MSRPCHF'
]

function stripSpaces(str: string): string {
  return str.replace(/\s+/g, '').replace(/[^a-zA-Z0-9]/g, '')
}

function mapKeysStripSpaces(obj: Record<string, any>): Record<string, any> {
  const out: Record<string, any> = {}
  for (const key in obj) {
    out[stripSpaces(key)] = obj[key]
  }
  return out
}

function getStyleValue(obj: Record<string, any>): any {
  return obj.Style ?? obj.style ?? obj.StyleNumber ?? obj.stylenumber
}

function hasStyleNumber(obj: Record<string, any>): boolean {
  const style = getStyleValue(obj)
  if (style == null) return false
  if (typeof style === 'number') return Number.isFinite(style)
  return String(style).trim().length > 0
}

function slugify(input: string): string {
  return String(input || '')
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[ÅåÄä]/g, 'a')
    .replace(/[Öö]/g, 'o')
    .replace(/&/g, 'and')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-|-$/g, '')
}

/**
 * Försöker tolka strängar av typ:
 *  - "Date(1900,0,6,12,0,0)" (Google export)
 *  - "180:00:00.000"
 * Annars returnerar numret om parsbar, annars null.
 *
 * Heuristik för Date(): pris ≈ day*24 + hour + minute/60
 * (Justera om källdata senare ändras.)
 */
function coercePrice(val: any): number | null {
  if (val == null || val === '') return null
  if (typeof val === 'number' && Number.isFinite(val)) return val

  const s = String(val).trim()
  const dateMatch = /^Date\((\d+),(\d+),(\d+),(\d+),(\d+),(\d+)\)$/.exec(s)
  if (dateMatch) {
    const day = parseInt(dateMatch[3], 10)
    const hour = parseInt(dateMatch[4], 10)
    const minute = parseInt(dateMatch[5], 10)
    const price = day * 24 + hour + minute / 60
    return Math.round(price * 100) / 100
  }

  const hourLike = /^(\d+):\d{2}:\d{2}/.exec(s)
  if (hourLike) {
    return parseInt(hourLike[1], 10)
  }

  const num = Number(s.replace(',', '.'))
  if (!Number.isNaN(num)) return num

  return null
}

export async function parseSheet(sheetId: string, sheetPage: string): Promise<CatalogItem[]> {
  const parser = new PublicGoogleSheetsParser(sheetId, sheetPage)
  const parsedData = await parser.parse()
  const normalized = parsedData.map(mapKeysStripSpaces)
  const filtered = normalized.filter(hasStyleNumber)

  return filtered.map((o: any) => {
    if (o.Style == null) o.Style = getStyleValue(o)

    // Ny toppnivåkategori
    const topCategory = o.Category || o['Category'] || 'Other'
    o.Category = topCategory
    o.CategorySlug = slugify(topCategory)

    // Bestäm underkategori (Subcategory ersätter WBCategory/ProductFamily)
    const sub = o.Subcategory || o.WBCategory || o.ProductFamily || o['ProductFamily'] || o['Product Family'] || 'Other'
    o.Subcategory = sub
    o.WBCategory = sub
    o.ProductFamily = sub
    o.ProductFamilySlug = slugify(sub)

    // Pris-coercion
    for (const key of PRICE_FIELDS) {
      if (key in o) {
        const coerced = coercePrice(o[key])
        if (coerced != null) o[key] = coerced
      }
    }

    return o as CatalogItem
  })
}
